/**
 * Countdown gauge (SVG) that sits over the THALIA core.
 * Normal: palette gradient arc (fraction of 24h cap) with a comet tail behind the progress dot.
 * Layers (back to front): breathing halo, counter-rotating tick rings (pointer parallax),
 * orbiting particles, scrolling hex band, glass disc, readout.
 * Final 5 min: arc drains over the 5-minute window, everything takes the urgency color,
 * rings spin faster, digits heartbeat. Blood Moon swaps accents via body.blood-moon vars.
 * All motion is CSS (transform/opacity) and disabled under prefers-reduced-motion.
 */
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { URGENCY_WINDOW, FINAL_MINUTE, type UrgencyState } from './urgency';

const C = 2 * Math.PI * 160;

export function fmtClock(s: number) {
  if (s < 0) return '--:--:--';
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  const p = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${p(h)}:${p(m)}:${p(sec)}` : `${p(m)}:${p(sec)}`;
}

const pt = (r: number, a: number) => [200 + r * Math.cos(a), 200 + r * Math.sin(a)] as const;

function Ticks() {
  const lines = useMemo(() => {
    const out: ReactElement[] = [];
    for (let i = 0; i < 120; i++) {
      const a = (i / 120) * Math.PI * 2 - Math.PI / 2;
      const major = i % 10 === 0;
      const [x1, y1] = pt(192, a), [x2, y2] = pt(major ? 180 : 187, a);
      const col = i < 36 ? 'var(--holo-tick-a)' : i < 60 ? 'var(--holo-tick-b)' : i < 76 ? 'var(--holo-tick-c)' : 'rgba(167,123,255,.2)';
      out.push(<line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={col} strokeWidth={major ? 1.4 : 1} strokeOpacity={major ? 1 : 0.55} />);
    }
    return out;
  }, []);
  return <g className="holo-ticks">{lines}</g>;
}

/** Inner counter-rotating ring: 60 fine ticks + 4 brackets. */
function MidTicks() {
  const lines = useMemo(() => {
    const out: ReactElement[] = [];
    for (let i = 0; i < 60; i++) {
      const a = (i / 60) * Math.PI * 2;
      const [x1, y1] = pt(174, a), [x2, y2] = pt(i % 5 === 0 ? 168 : 171, a);
      out.push(<line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />);
    }
    return out;
  }, []);
  return (
    <g className="hg-mid">
      {lines}
      {[0, 1, 2, 3].map(q => {
        const a0 = q * Math.PI / 2 - 0.16, a1 = q * Math.PI / 2 + 0.16;
        const [x0, y0] = pt(178, a0), [x1, y1] = pt(178, a1);
        return <path key={q} d={`M${x0} ${y0} A178 178 0 0 1 ${x1} ${y1}`} className="hg-bracket" />;
      })}
    </g>
  );
}

const PARTICLES = Array.from({ length: 14 }, (_, i) => ({
  r: 146 + ((i * 37) % 48),          // 146..193
  a: (i * 137.5) % 360,
  d: 18 + ((i * 7) % 22),            // seconds per orbit
  s: 0.8 + ((i * 5) % 4) * 0.35,
  o: 0.25 + ((i * 3) % 5) * 0.1,
  rev: i % 3 === 0,
}));

export default function HoloGauge({ timeRemaining, roundId, urgency }: { timeRemaining: number; roundId: number; urgency: UrgencyState }) {
  const t = Math.max(0, timeRemaining);
  const [jump, setJump] = useState(0);
  const prev = useRef(timeRemaining);
  const svgRef = useRef<SVGSVGElement>(null);
  useEffect(() => {
    if (timeRemaining > prev.current + 1 && prev.current >= 0) {
      setJump(j => j + 1);
      const id = setTimeout(() => setJump(j => -Math.abs(j)), 1400);
      prev.current = timeRemaining;
      return () => clearTimeout(id);
    }
    prev.current = timeRemaining;
  }, [timeRemaining]);
  const jumping = jump > 0;

  // Pointer parallax (cheap: two CSS vars, rAF-throttled)
  useEffect(() => {
    const el = svgRef.current;
    if (!el || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    let raf = 0;
    const move = (e: PointerEvent) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const b = el.getBoundingClientRect();
        el.style.setProperty('--px', (((e.clientX - b.left) / b.width - 0.5) * 2).toFixed(3));
        el.style.setProperty('--py', (((e.clientY - b.top) / b.height - 0.5) * 2).toFixed(3));
      });
    };
    const leave = () => { el.style.setProperty('--px', '0'); el.style.setProperty('--py', '0'); };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerleave', leave);
    return () => { el.removeEventListener('pointermove', move); el.removeEventListener('pointerleave', leave); cancelAnimationFrame(raf); };
  }, []);

  const u = urgency.active;
  const frac = timeRemaining < 0 ? 0 : u ? Math.min(1, urgency.smoothT / URGENCY_WINDOW) : Math.min(1, t / 86400);
  const finalMinute = timeRemaining >= 0 && timeRemaining < FINAL_MINUTE;
  const digitColor = u ? urgency.color : '#e3f6f8';
  const glow = u ? urgency.color : 'rgba(var(--acc-rgb),.6)';
  const dotCol = u ? urgency.color : 'var(--arc-end, #f058f6)';
  const angle = frac * Math.PI * 2 - Math.PI / 2;
  const [dx, dy] = pt(160, angle);

  // Comet tail: fading beads trailing the dot along the arc (never past the arc start)
  const tail = useMemo(() => {
    const out: { x: number; y: number; r: number; o: number }[] = [];
    const span = frac * Math.PI * 2;
    for (let k = 1; k <= 12; k++) {
      const da = k * 0.032;
      if (da > span) break;
      const [x, y] = pt(160, angle - da);
      out.push({ x, y, r: 5.2 - k * 0.35, o: 0.75 * (1 - k / 13) });
    }
    return out;
  }, [frac, angle]);

  const clock = fmtClock(timeRemaining);
  const long = t >= 3600 || timeRemaining < 0;
  const colon = clock.lastIndexOf(':');
  const head = clock.slice(0, colon + 1), secs = clock.slice(colon + 1);
  const digitProps = {
    x: 200, y: 221, textAnchor: 'middle' as const, fontFamily: 'JetBrains Mono', fontWeight: 600,
    fontSize: long ? 50 : 62, letterSpacing: long ? 2 : 3,
  };

  return (
    <svg ref={svgRef} className={`holo-gauge hg2 ${u ? 'is-urgent' : ''} ${finalMinute ? 'is-final' : ''}`} viewBox="0 0 400 400" role="timer" aria-label={`Time left ${clock}`}>
      <defs>
        <linearGradient id="holo-gg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" style={{ stopColor: 'var(--c)' }} />
          <stop offset=".55" style={{ stopColor: 'var(--v2)' }} />
          <stop offset="1" style={{ stopColor: 'var(--arc-end, #f058f6)' }} />
        </linearGradient>
        <radialGradient id="holo-glass" cx=".5" cy=".5" r=".5">
          <stop offset="0" stopColor="#04061c" stopOpacity=".95" />
          <stop offset=".72" stopColor="#060a2a" stopOpacity=".88" />
          <stop offset="1" stopColor="#0a1040" stopOpacity=".6" />
        </radialGradient>
        <radialGradient id="hg-halo" cx=".5" cy=".5" r=".5">
          <stop offset=".78" style={{ stopColor: 'var(--u-color)' }} stopOpacity="0" />
          <stop offset=".9" style={{ stopColor: u ? 'var(--u-color)' : 'var(--v2)' }} stopOpacity=".22" />
          <stop offset="1" style={{ stopColor: 'var(--u-color)' }} stopOpacity="0" />
        </radialGradient>
        <pattern id="hg-hex" width="12" height="20.78" patternUnits="userSpaceOnUse">
          <path d="M3 0h6l3 5.2-3 5.2H3L0 5.2zM-3 10.39h6l3 5.2-3 5.2h-6M9 10.39h6l3 5.2-3 5.2H9" fill="none" stroke="currentColor" strokeWidth=".6" />
        </pattern>
        <mask id="hg-band">
          <circle cx="200" cy="200" r="152" fill="#fff" />
          <circle cx="200" cy="200" r="134" fill="#000" />
        </mask>
      </defs>

      {/* breathing outer halo */}
      <circle className="hg-halo" cx="200" cy="200" r="200" fill="url(#hg-halo)" />

      {/* counter-rotating rings with parallax depth */}
      <g className="hg-par hg-d3"><g className="hg-spin hg-spin-a"><Ticks /></g></g>
      <g className="hg-par hg-d2"><g className="hg-spin hg-spin-b" style={{ color: u ? urgency.color : 'var(--c)' }}><MidTicks /></g></g>

      {/* orbiting particles */}
      <g className="hg-par hg-d1 hg-parts" style={{ color: u ? urgency.color : 'var(--c)' }}>
        {PARTICLES.map((p, i) => (
          <g key={i} className={`hg-orb ${p.rev ? 'rev' : ''}`} style={{ animationDuration: `${p.d}s` }}>
            <circle transform={`rotate(${p.a} 200 200)`} cx={200 + p.r} cy="200" r={p.s} fill="currentColor" opacity={p.o} />
          </g>
        ))}
      </g>

      {/* scrolling hex / data band */}
      <g mask="url(#hg-band)" className="hg-hexband" style={{ color: u ? urgency.color : 'var(--c)' }}>
        <g className="hg-spin hg-spin-c"><rect x="40" y="40" width="320" height="320" fill="url(#hg-hex)" /></g>
      </g>
      <circle cx="200" cy="200" r="152" fill="none" className="hg-hairline" />

      {/* progress track, gradient arc, comet tail, dot */}
      <circle cx="200" cy="200" r="160" fill="none" style={{ stroke: 'rgba(var(--acc-rgb),.12)' }} strokeWidth="6" />
      <circle
        cx="200" cy="200" r="160" fill="none"
        stroke={u ? urgency.color : 'url(#holo-gg)'} strokeWidth="6" strokeLinecap="round"
        strokeDasharray={`${(C * frac).toFixed(1)} ${C.toFixed(1)}`}
        transform="rotate(-90 200 200)"
        className="holo-arc"
        style={{ filter: `drop-shadow(0 0 ${u ? 6 + urgency.level * 10 : 7}px ${u ? urgency.color : 'var(--c)'})` }}
      />
      <g className="hg-comet" style={{ color: dotCol, filter: `drop-shadow(0 0 5px ${dotCol})` }}>
        {tail.map((b, i) => <circle key={i} cx={b.x} cy={b.y} r={b.r} fill="currentColor" opacity={b.o} />)}
        <circle cx={dx} cy={dy} r="10" fill="currentColor" opacity=".18" className="hg-dot-glow" />
        <circle cx={dx} cy={dy} r="6" fill="currentColor" />
        <circle cx={dx} cy={dy} r="2.4" fill="#fff" opacity=".9" />
      </g>

      {/* +30s shockwave */}
      {jumping && (
        <g key={`sw${jump}`} className="hg-shock" style={{ color: 'var(--arc-end, #f058f6)' }}>
          <circle cx="200" cy="200" r="150" fill="none" stroke="currentColor" strokeWidth="3" className="w1" />
          <circle cx="200" cy="200" r="150" fill="none" stroke="currentColor" strokeWidth="1.5" className="w2" />
        </g>
      )}

      {/* readout glass */}
      <circle cx="200" cy="200" r="132" fill="url(#holo-glass)" />
      <circle cx="200" cy="200" r="132" fill="none" stroke={u ? urgency.color : 'rgba(127,140,240,.28)'} strokeOpacity={u ? 0.4 : 1} strokeWidth="1" />
      <text x="200" y="156" textAnchor="middle" fill={u ? urgency.color : '#a9c4d8'} fontFamily="Rajdhani" fontWeight={700} fontSize="13" letterSpacing="4.5">
        {u ? 'FINAL COUNTDOWN' : `ROUND ${roundId}`}
      </text>
      <g className="holo-digits-wrap">
        <g style={{ filter: `drop-shadow(0 0 ${u ? 8 + urgency.level * 12 : 5}px ${glow})` }}>
          {/* static head (MM: or HH:MM:) + invisible seconds keep layout identical */}
          <text {...digitProps} className={`holo-digits ${jumping ? 'jump' : ''}`} fill={jumping ? '#f058f6' : digitColor}
            stroke="rgba(4,6,28,.85)" strokeWidth="4" paintOrder="stroke">
            <tspan>{head}</tspan><tspan fill="transparent" stroke="transparent">{secs}</tspan>
          </text>
          {/* seconds: re-keyed each tick so they flip in */}
          <g key={secs} className="hg-flip">
            <text {...digitProps} className={`holo-digits ${jumping ? 'jump' : ''}`} fill={jumping ? '#f058f6' : digitColor}
              stroke="rgba(4,6,28,.85)" strokeWidth="4" paintOrder="stroke">
              <tspan fill="transparent" stroke="transparent">{head}</tspan><tspan>{secs}</tspan>
            </text>
          </g>
        </g>
      </g>
      {jumping && <text key={`ft${jump}`} x="200" y="120" textAnchor="middle" className="hg-float" fill="var(--arc-end, #f058f6)" fontFamily="JetBrains Mono" fontWeight={700} fontSize="20">+30s</text>}
      <text x="200" y="258" textAnchor="middle" fill={u ? urgency.color : '#c58cf2'} fontFamily="Rajdhani" fontWeight={600} fontSize="13" letterSpacing="3">
        {jumping ? '+30s ADDED' : '+30s PER KEY'}
      </text>
    </svg>
  );
}
